import React from "react";
import { Grid, Box, Typography, Avatar, Chip, Button, Dialog, DialogContent, IconButton, LinearProgress } from "@mui/material";
import { CiCalendar } from "react-icons/ci";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StatusBadge from "components/badge/StatusBadge";
import moment from "moment";
import { useGetMyTaskHistoryQuery, useGetWorkflowDetailQuery } from "store/slices/apis/workFlowSlice";
import WorkflowMetaCard from "components/WorkflowMetaCard";
import { RxCross2 } from "react-icons/rx";
import { FaAngleRight } from "react-icons/fa6";

const WorkflowHistoryDetail = ({ open, onClose, cardData }) => {

    const {
        data: historyResponse,
        isLoading: isHistoryLoading,
        error: historyError,
    } = useGetMyTaskHistoryQuery(
        { instanceId: cardData?.workflowInstanceId, type: cardData?.type },
        { skip: !cardData?.workflowInstanceId }
    );

    const {
        data: scheduleData,
        isLoading: isScheduleLoading,
        error: scheduleError,
    } = useGetWorkflowDetailQuery(
        cardData?.workflowId,
        { skip: !cardData?.workflowId }
    );

    const workflowSteps = historyResponse?.data?.workflowSteps ?? [];
    const futureSteps = historyResponse?.data?.futureSteps ?? [];
    const schedule = scheduleData?.data;

    const getFormattedDate = (date) =>
        date ? moment(date).format("DD MMM YYYY") : "";

    const getInitials = (name) =>
        (name || "NA")
            .split(" ")
            .filter(Boolean)
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);

    const renderAttachmentList = (stepAttachments = []) => {
        if (!stepAttachments.length) return null;

        return (
            <Box
                sx={{
                    mt: "6px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    width: "100%",
                }}
            >
                {stepAttachments.map((att, attachmentIndex) => (
                    <Box
                        key={att.id || `${att.fileName || "attachment"}-${attachmentIndex}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: "6px",
                            border: "1px solid #ECECEC",
                            p: "4px 8px",
                            bgcolor: "#FFFFFF",
                            gap: 1,
                            maxWidth: 320,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 22,
                                    height: 26,
                                    borderRadius: "4px",
                                    bgcolor: "#E7F0FD",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "9px",
                                    fontWeight: 600,
                                    color: "#0A65E4",
                                    fontFamily: "Outfit",
                                }}
                            >
                                {(att.fileType || "file").toUpperCase().slice(0, 4)}
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#2C274A",
                                    fontFamily: "Outfit",
                                    maxWidth: 200,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {att.fileName || "Attachment"}
                            </Typography>
                        </Box>

                        <Button
                            size="small"
                            component="a"
                            href={`${att.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                fontFamily: "Outfit",
                                color: "#0A65E4",
                                minWidth: "auto",
                            }}
                        >
                            View
                        </Button>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderChildApprovals = (childStepApprovals = [], parentId) => {
        if (!childStepApprovals.length) return null;

        return (
            <Box
                sx={{
                    mt: 1.5,
                    pl: 2.5,
                    borderLeft: "2px solid #E0E0EA",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                {childStepApprovals.map((child, childIndex) => {
                    const childUserName =
                        child.approverName || child.roleName || "Unknown approver";
                    const childStepName = child.childStepName || "";
                    const childPrimaryText = child.comment || "Reviewed";
                    const childCreatedOn = child.approvedDate;
                    const childTimeAgo = childCreatedOn
                        ? moment(childCreatedOn).fromNow()
                        : "";

                    return (
                        <Box
                            key={child.id || `${parentId}-child-${childIndex}`}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#3A345C",
                                    fontFamily: "Outfit",
                                }}
                            >
                                {childPrimaryText}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 400,
                                    color: "#8A879C",
                                    fontFamily: "Outfit",
                                }}
                            >
                                {childStepName}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 400,
                                    color: "#8A879C",
                                    fontFamily: "Outfit",
                                }}
                            >
                                {childUserName}
                                {childTimeAgo && <> &bull; {childTimeAgo}</>}
                                {child.status && <> &bull; {child.status}</>}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderHistoryItem = (item, index) => {
        const userName =
            item.approverRoleName || item.performedBy || "Unknown user";
        const createdOn = item.completedDate || item.createdOn || item.timestamp;
        const timeAgo = createdOn ? moment(createdOn).fromNow() : "";
        const stepAttachments = item.attachments || [];
        const childStepApprovals = item.childStepApprovals || [];
        const workflowStepName = item.workflowStep || "";
        const isRejected = item.actionTaken === "Rejected";
        const primaryText = item.comment || item.action;

        return (
            <Box
                key={item.id || `history-${index}`}
                sx={{
                    display: "flex",
                    gap: "16px",
                    width: "100%",
                }}
            >
                <Avatar
                    sx={{
                        width: 28,
                        height: 28,
                        bgcolor: "#140F36",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        mt: "4px",
                    }}
                >
                    {getInitials(userName)}
                </Avatar>

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {isRejected && (
                            <CancelOutlinedIcon
                                sx={{
                                    fontSize: 16,
                                    color: "#E53935",
                                }}
                            />
                        )}

                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                fontFamily: "Outfit",
                                color: isRejected ? "#E53935" : "#2C274A",
                            }}
                        >
                            {primaryText}
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            mt: "2px",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#726F86",
                            fontFamily: "Outfit",
                        }}
                    >
                        {workflowStepName}
                    </Typography>

                    <Typography
                        sx={{
                            mt: "2px",
                            fontSize: "12px",
                            fontWeight: 400,
                            color: "#726F86",
                            fontFamily: "Outfit",
                        }}
                    >
                        {userName}
                        {timeAgo && <> &bull; {timeAgo}</>}
                    </Typography>

                    {renderAttachmentList(stepAttachments)}
                    {renderChildApprovals(childStepApprovals, item.id || index)}
                </Box>
            </Box>
        );
    };

    const getStepTitle = (item) =>
        item?.workflowStep ||
        item?.stepName ||
        item?.childStepName ||
        item?.action ||
        item?.comment ||
        item?.description ||
        "Workflow step";

    const getStepOwner = (item) =>
        item?.approverRoleName ||
        item?.roleName ||
        item?.assignedToRoleName ||
        item?.performedBy ||
        "";

    const currentStageLabel = schedule?.workflowStep || cardData?.currentStep || "Current step";
    const totalStepCount = workflowSteps.length + futureSteps.length + 1;
    const completedStepCount = workflowSteps.length;
    const workflowProgressValue = totalStepCount
        ? Math.round((completedStepCount / totalStepCount) * 100)
        : 0;
    const forwardProgressSteps = [
        {
            id: "current-step",
            variant: "current",
            title: currentStageLabel,
            owner: cardData?.pendingWith || cardData?.approverRoleName || "",
            caption: "Current stage",
            status: cardData?.status || "In Progress",
        },
        ...futureSteps.map((item, index) => ({
            id: item.id || `future-${index}`,
            variant: "future",
            title: getStepTitle(item),
            owner: getStepOwner(item),
            caption:
                index === 0
                    ? "Next in line"
                    : item?.dueDate || item?.expectedDate
                        ? `Expected by ${getFormattedDate(item.dueDate || item.expectedDate)}`
                        : "Upcoming",
            status: item.status || "Pending",
        })),
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: "8px",
                    width: "100%",
                    margin: "16px",
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Grid container spacing={0}>
                    {/* HEADER */}
                    <Grid
                        size={12}
                        sx={{
                            p: { xs: "10px 15px", md: "15px 20px 13px 20px" },
                            bgcolor: "#ECECEC",
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid size={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography
                                    sx={{
                                        fontSize: { xs: "22px", md: "27px" },
                                        fontWeight: 600,
                                        color: "#140F36",
                                        fontFamily: "Outfit",
                                    }}
                                >
                                    Task Details
                                </Typography>
                                <IconButton onClick={onClose}>
                                    <RxCross2 />
                                </IconButton>

                            </Grid>
                            <Grid size={12}>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        color: "#433F5E",
                                        fontFamily: "Outfit",
                                    }}
                                >
                                    View the task detail and take actions
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* SUMMARY CARD */}
                    <Grid size={12} sx={{ p: { xs: "12px", md: "16px 34px 45px 20px" } }}>
                        <Box
                            sx={{
                                border: "1px solid #ECECEC",
                                borderRadius: "5px",
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                gap: "11px",
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", sm: "48px" },
                                    minHeight: { xs: "70px", sm: "160px" },
                                    mr: { xs: 0, sm: "15px" },
                                    bgcolor: "#6054BB99",
                                }}
                            />

                            <Box sx={{ p: "8px 15px 7px 0px", width: "100%" }}>
                                {/* Row 1 */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", md: "center" },
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                        <Avatar
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                bgcolor: "#6054BB",
                                                color: "#fff",
                                                fontSize: "14px",
                                                fontWeight: "bold",
                                                textTransform: "uppercase",
                                            }}
                                        />
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "16px",
                                                    fontWeight: 500,
                                                    color: "#140F36",
                                                    fontFamily: "Outfit",
                                                    whiteSpace: { xs: "normal", md: "nowrap" },
                                                }}
                                            >
                                                {cardData?.projectName || ""}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                    color: "#433F5E",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                Description: {cardData?.description || ""}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: { xs: "4px", md: "12px" },
                                            alignItems: { xs: "flex-start", md: "center" },
                                            mt: { xs: "10px", md: 0 },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#433F5E",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Initiated On
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                gap: "8px",
                                                alignItems: "center",
                                                backgroundColor: "#E7E7EB",
                                                p: "2px 6px",
                                                borderRadius: "5px",
                                            }}
                                        >
                                            <CiCalendar size={16} color="#5B5772" />
                                            <Typography
                                                sx={{
                                                    color: "#5B5772",
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                {getFormattedDate(cardData?.pendingFrom || cardData?.createdDate)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Row 2 */}
                                <Box
                                    sx={{
                                        mt: "12px",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", md: "center" },
                                    }}
                                >
                                    <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                color: "#433F5E",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Type:
                                        </Typography>
                                        <Chip
                                            sx={{
                                                height: "17px",
                                                borderRadius: "5px",
                                                backgroundColor: "#D0CFD7",
                                                px: "6px",
                                            }}
                                            label={
                                                <Box
                                                    sx={{
                                                        fontFamily: "Outfit",
                                                        fontWeight: 600,
                                                        fontSize: "10px",
                                                        color: "#433F5E",
                                                    }}
                                                >
                                                    {cardData?.type || ""}
                                                </Box>
                                            }
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: { xs: "4px", md: "12px" },
                                            alignItems: { xs: "flex-start", md: "center" },
                                            mt: { xs: "10px", md: 0 },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#433F5E",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Due On
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                gap: "8px",
                                                alignItems: "center",
                                                backgroundColor: "#E7E7EB",
                                                p: "2px 6px",
                                                borderRadius: "5px",
                                            }}
                                        >
                                            <CiCalendar size={16} color="#5B5772" />
                                            <Typography
                                                sx={{
                                                    color: "#5B5772",
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                {getFormattedDate(cardData?.dueDate)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Row 3 */}
                                <Box
                                    sx={{
                                        mt: "15px",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", md: "center" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "5px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    color: "#433F5E",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                Initiated By:
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    color: "#433F5E",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                {cardData?.createdBy || "Shanti Nautiyal"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "12px",
                                            alignItems: "center",
                                            mt: { xs: "10px", md: 0 },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                color: "#433F5E",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Workflow status
                                        </Typography>
                                        <StatusBadge status={cardData?.status || ""} />
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        mt: "18px",
                                        p: "14px",
                                        borderRadius: "3px",
                                        bgcolor: "#FBFBFD",
                                        border: "1px solid #ECEFFC",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: { xs: "flex-start", md: "center" },
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: "15px",
                                                    fontWeight: 600,
                                                    color: "#2C274A",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                Workflow Progress
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: "12px",
                                                    fontWeight: 400,
                                                    color: "#726F86",
                                                    fontFamily: "Outfit",
                                                    mt: 0.4,
                                                }}
                                            >
                                                {completedStepCount} of {totalStepCount} stages completed
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: "12px",
                                                alignItems: { xs: "flex-start", md: "center" },
                                                flexDirection: { xs: "column", md: "row" },
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    color: "#433F5E",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                Current Stage:
                                            </Typography>
                                            <StatusBadge status={currentStageLabel} />
                                        </Box>
                                    </Box>

                                    <LinearProgress
                                        variant="determinate"
                                        value={workflowProgressValue}
                                        sx={{
                                            mt: 1.5,
                                            height: 8,
                                            borderRadius: 999,
                                            bgcolor: "#E8ECF4",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 999,
                                                background: "linear-gradient(90deg, #6054BB 0%, #0A65E4 100%)",
                                            },
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            mt: 1.75,
                                            display: "flex",
                                            alignItems: "stretch",
                                            overflowX: "auto",
                                            pb: 1,
                                            pr: 1,
                                            scrollbarWidth: "thin",
                                            "&::-webkit-scrollbar": { height: 6 },
                                            "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#D5DCEA",
                                                borderRadius: 999,
                                            },
                                        }}
                                    >
                                        {forwardProgressSteps.map((step, index) => {
                                            const isCurrent = step.variant === "current";
                                            const isNext = step.variant === "future" && index === 1;

                                            return (
                                                <React.Fragment key={step.id || index}>
                                                    <Box
                                                        sx={{
                                                            minWidth: { xs: 220, md: 250 },
                                                            maxWidth: { xs: 220, md: 250 },
                                                            borderRadius: "3px",
                                                            border: isCurrent
                                                                ? "1px solid #A8B8FF"
                                                                : isNext
                                                                    ? "1px dashed #BFD2F6"
                                                                    : "1px dashed #D7DFEC",
                                                            bgcolor: isCurrent ? "#EEF1FF" : "#F8FAFD",
                                                            p: "12px 14px",
                                                            boxShadow: isCurrent ? "0 6px 18px rgba(96, 84, 187, 0.10)" : "none",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "11px",
                                                                    fontWeight: 700,
                                                                    letterSpacing: "0.3px",
                                                                    color: isCurrent ? "#6054BB" : "#72809A",
                                                                    textTransform: "uppercase",
                                                                    fontFamily: "Outfit",
                                                                }}
                                                            >
                                                                {isCurrent ? "Current" : isNext ? "Next" : "Upcoming"}
                                                            </Typography>
                                                            <StatusBadge status={step.status || (isCurrent ? "In Progress" : "Pending")} />
                                                        </Box>

                                                        <Typography
                                                            sx={{
                                                                fontSize: "13px",
                                                                fontWeight: 600,
                                                                color: "#2C274A",
                                                                lineHeight: 1.4,
                                                                fontFamily: "Outfit",
                                                            }}
                                                        >
                                                            {step.title}
                                                        </Typography>

                                                        {step.owner && (
                                                            <Typography
                                                                sx={{
                                                                    mt: 0.6,
                                                                    fontSize: "11px",
                                                                    fontWeight: 500,
                                                                    color: "#5F6B84",
                                                                    fontFamily: "Outfit",
                                                                }}
                                                            >
                                                                {step.owner}
                                                            </Typography>
                                                        )}

                                                        <Typography
                                                            sx={{
                                                                mt: 0.8,
                                                                fontSize: "11px",
                                                                fontWeight: 400,
                                                                color: "#8A879C",
                                                                fontFamily: "Outfit",
                                                            }}
                                                        >
                                                            {step.caption}
                                                        </Typography>
                                                    </Box>

                                                    {index < forwardProgressSteps.length - 1 && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                color: "#B6C0D4",
                                                                px: 0.5,
                                                            }}
                                                        >
                                                            <FaAngleRight size={14} />
                                                        </Box>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Box>
                                </Box>

                                {/* Row 4 */}
                                <Box
                                    sx={{
                                        mt: "8px",
                                        display: "flex",
                                        flexDirection: { xs: "column", md: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", md: "center" },
                                    }}
                                >
                                    {cardData?.files?.length > 0 && (
                                        <Box
                                            sx={{
                                                mt: "6px",
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "4px",
                                                width: "100%",
                                            }}
                                        >
                                            {cardData?.files?.map((att) => (
                                                <Box
                                                    key={att?.fileId}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        borderRadius: "6px",
                                                        border: "1px solid #ECECEC",
                                                        p: "4px 8px",
                                                        bgcolor: "#FFFFFF",
                                                        gap: 1,
                                                        maxWidth: 320,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 22,
                                                                height: 26,
                                                                borderRadius: "4px",
                                                                bgcolor: "#E7F0FD",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "9px",
                                                                fontWeight: 600,
                                                                color: "#0A65E4",
                                                                fontFamily: "Outfit",
                                                            }}
                                                        >
                                                            {(att.fileType || "file").toUpperCase().slice(0, 4)}
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "12px",
                                                                fontWeight: 500,
                                                                color: "#2C274A",
                                                                fontFamily: "Outfit",
                                                                maxWidth: 200,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {att?.fileName || "Attachment"}
                                                        </Typography>
                                                    </Box>

                                                    <Button
                                                        size="small"
                                                        component="a"
                                                        href={`${att?.filePath}`}   // 👈 your download link
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            textTransform: "none",
                                                            fontSize: "11px",
                                                            fontFamily: "Outfit",
                                                            color: "#0A65E4",
                                                            minWidth: "auto",
                                                        }}
                                                    >
                                                        View
                                                    </Button>

                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                                {/* Row 3 */}
                                <WorkflowMetaCard
                                    cardData={scheduleData?.data}
                                    StatusBadge={StatusBadge}
                                    title="Metadata"
                                    columns={2}
                                    hiddenKeys={["DesignTrackerId"]} // optional
                                    labelMap={{
                                        DrgNo: "Drawing No",
                                        TargetDate: "Target Date",
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* BODY: HISTORY + ACTION */}
                    <Grid size={12}>
                        <Grid
                            container
                            rowSpacing={{ xs: 2, md: 0 }}
                            columnSpacing={3}
                            sx={{ p: { xs: "0px 16px 22px 16px", md: "0px 34px 22px 20px" } }}
                        >
                            {/* LEFT: HISTORY */}
                            <Grid size={{ xs: 12, md: 12 }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "14px",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            fontWeight: 500,
                                            color: "#2C274A",
                                            fontFamily: "Outfit",
                                        }}
                                    >
                                        Workflow History
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: "6px 10px",
                                            bgcolor: "#E7F0FD",
                                            borderRadius: "5px",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#0A65E4",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            History
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        mt: "10px",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "14px",
                                        alignItems: "flex-start",
                                        //maxHeight: 420,
                                        pr: 1,
                                        overflowY: "auto",
                                    }}
                                >
                                    {/* Loading */}
                                    {isHistoryLoading && (
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#726F86",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Loading history...
                                        </Typography>
                                    )}

                                    {/* Error */}
                                    {historyError && !isHistoryLoading && (
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#FF383C",
                                                fontFamily: "Outfit",
                                            }}
                                        >
                                            Failed to load history.
                                        </Typography>
                                    )}

                                    {/* Empty */}
                                    {!isHistoryLoading &&
                                        !historyError &&
                                        workflowSteps.length === 0 && (
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 400,
                                                    color: "#726F86",
                                                    fontFamily: "Outfit",
                                                }}
                                            >
                                                No workflow history available for this task yet.
                                            </Typography>
                                        )}

                                    {/* Items */}
                                    {!isHistoryLoading &&
                                        !historyError &&
                                        workflowSteps.length > 0 &&
                                        [...workflowSteps].reverse().map(renderHistoryItem)}

                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default WorkflowHistoryDetail;
